// tenantModulesCatalog.js
// Catalog for the per-customer "Modules" panel (Customers tab -> Modules
// button -> CustomerModulesModal). This is a UI-side COPY of the tenant
// module key spaces, not a source of truth by itself.
//
// KEEP IN SYNC with:
//   - my-app/src/utils/permissionsConfig.js       (human labels, per-employee UI)
//   - my-app/functions/allFunctions/shared/tenantModules.js  (server catalog + enforcement)
// Do NOT rename/add/remove keys here without updating both of those files.
//
// Storage contract (server-written only, via adminDashboardF-setCustomerModules):
//   customers/{cid}.modules = { web: {key:bool}, mobile: {key:bool}, restricted: {key:bool} }
// ABSENT map or ABSENT key = ENABLED (grandfathers every existing customer).
// Tenant gating operates at this TOP-LEVEL key granularity only - there is
// no per-sub-feature tenant toggle (that finer grain stays a per-employee
// concern on the "User Access" page inside my-app).

// Web modules (12 keys). "gc" (Simple GC) intentionally lives under
// restricted below, not here - it moved to the explicit-grant restricted
// system in the 2026-08-31 round.
export const TENANT_WEB_MODULES = [
  { key: "jobsites", label: "Jobsites" },
  { key: "assets", label: "Assets" },
  { key: "employees", label: "Employees" },
  { key: "workscheduler", label: "Work Scheduler" },
  { key: "products", label: "Product Library" },
  { key: "customdocs", label: "Document Templates" },
  { key: "committee", label: "Committee meetings" },
  { key: "walkthroughs", label: "Walkthrough Reports" },
  { key: "safety", label: "Safety" },
  { key: "chat", label: "Chat / Messages" },
  { key: "warehouse", label: "Simple warehouse / Inventory", addOn: true },
  { key: "fleet", label: "Fleet GPS tracking", addOn: true },
  { key: "fleetCan", label: "Fleet GPS: CAN odometer (premium)", addOn: true },
];

// Mobile app features (20 keys).
export const TENANT_MOBILE_MODULES = [
  { key: "timesheetviewer", label: "Timesheet Viewer" },
  { key: "tailgatemeetings", label: "Tailgate Meetings" },
  { key: "psi", label: "PSI (Pre-Job Safety)" },
  { key: "equipmenttoolschecklist", label: "Equipment & Tools Checklist" },
  { key: "vehiclechecklist", label: "Daily Vehicle Checklist" },
  { key: "monthlyvehiclechecklist", label: "Monthly Vehicle Checklist" },
  { key: "ppeinspection", label: "PPE Inspection" },
  { key: "photoreport", label: "Photo Reports" },
  { key: "progresstracker", label: "Progress Tracker" },
  { key: "sitejournal", label: "Site Journal" },
  { key: "expenses", label: "Expenses" },
  { key: "signaturepad", label: "Signature Pad" },
  { key: "blueprints", label: "Blueprints" },
  { key: "customdocs", label: "Custom Documents" },
  { key: "incidents", label: "Incident Reports" },
  { key: "inspections", label: "Site Inspections" },
  { key: "corrective", label: "Corrective Actions" },
  { key: "chat", label: "Chat / Messages" },
  { key: "warehouse", label: "Simple warehouse / Inventory", addOn: true },
  { key: "walkthroughs", label: "Walkthrough Recording" },
];

// Premium / restricted pages that are tenant-manageable (5 keys). Note:
// "useraccess" and "notificationemails" are DELIBERATELY EXCLUDED - core
// admin plumbing, never sellable-off, so they never appear in this panel.
export const TENANT_RESTRICTED_MODULES = [
  { key: "payroll", label: "Payroll" },
  { key: "companyoverview", label: "Company Overview" },
  { key: "estimator", label: "Simple Job Estimator" },
  { key: "integrations", label: "Integrations" },
  { key: "gc", label: "Simple GC", addOn: true },
];

// Keys that exist in BOTH web and mobile and MUST toggle together (Opus
// review 2026-08-31, finding 1): the server's permission lists pair
// "web:<key>" with "mobile:<key>" on SHARED callables, and the tenant gate
// passes when EITHER side is enabled (ANY-allows) - so per-surface toggling
// of these four is not server-enforceable. The modal renders each as ONE
// combined "web + mobile" row that writes both spaces identically. The
// remaining mobile-only keys are honest UI switches (they hide features in
// the worker app; their callables are shared with always-on web modules).
export const LINKED_WEB_MOBILE_KEYS = ["warehouse", "chat", "customdocs", "walkthroughs"];

// Combined display labels for the linked rows (each replaces a web label +
// a mobile label pair).
export const LINKED_COMBINED_LABELS = {
  warehouse: "Simple warehouse / Inventory",
  chat: "Chat / Messages",
  customdocs: "Custom Documents & Templates",
  walkthroughs: "Walkthrough Reports & Recording",
};

// Section metadata driving the modal's three groups, in display order.
export const TENANT_MODULE_SECTIONS = [
  { space: "web", title: "Web modules", items: TENANT_WEB_MODULES },
  { space: "mobile", title: "Mobile app features", items: TENANT_MOBILE_MODULES },
  { space: "restricted", title: "Premium pages", items: TENANT_RESTRICTED_MODULES },
];

// Builds the full explicit {web:{},mobile:{},restricted:{}} map with every
// catalog key set to `true`.
export function buildFullTenantModules() {
  const build = (items) => {
    const out = {};
    items.forEach((item) => {
      out[item.key] = true;
    });
    return out;
  };
  return {
    web: build(TENANT_WEB_MODULES),
    mobile: build(TENANT_MOBILE_MODULES),
    restricted: build(TENANT_RESTRICTED_MODULES),
  };
}

// Merges a customer's stored `modules` map (possibly absent/partial/from an
// older or newer catalog) over the full-enabled defaults, matching the
// server contract exactly: absent map or absent key = enabled (true); only
// an explicit `false` disables. Always returns a FULL explicit map covering
// exactly today's catalog keys, ready to send straight back to
// adminDashboardF-setCustomerModules after checkbox edits.
export function normalizeTenantModules(stored) {
  const merge = (space, items) => {
    const out = {};
    items.forEach((item) => {
      out[item.key] = stored?.[space]?.[item.key] === false ? false : true;
    });
    return out;
  };
  return {
    web: merge("web", TENANT_WEB_MODULES),
    mobile: merge("mobile", TENANT_MOBILE_MODULES),
    restricted: merge("restricted", TENANT_RESTRICTED_MODULES),
  };
}
